// src/Pages/AgregarUsuario.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ref, get, child } from "firebase/database";
import { FiEdit2 } from "react-icons/fi";
import AttendanceTable from "../components/Table/AttendanceTable";
import ModalAgregarUsuario from "./ModalAgregarUsuario";
import { dbAsistencia } from "../firebase";

import {
  crearUsuarioCompleto,
  cambiarEstadoUsuario,
  actualizarUsuarioCompleto,
} from "../services/adminUsuariosService";

import "../styles/agregarUsuario.css";

export default function AgregarUsuario() {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadingList, setLoadingList] = useState(false);

  const [managers, setManagers] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalKey, setModalKey] = useState(0);

  const [creating, setCreating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const rootRef = ref(dbAsistencia);
        const snap = await get(child(rootRef, "managers"));
        if (snap.exists()) setManagers(snap.val());
      } catch (e) {
        console.error("Error cargando managers:", e);
      }
    };
    loadManagers();
  }, []);

  const loadUsers = async () => {
    setLoadingList(true);
    try {
      const rootRef = ref(dbAsistencia);
      const snap = await get(child(rootRef, "users"));
      const list = [];

      if (snap.exists()) {
        const data = snap.val();
        Object.entries(data).forEach(([authUid, u]) => {
          list.push({
            id: authUid,
            name: u?.Name || "",
            dni: u?.UUID != null ? String(u.UUID) : "",
            uid: Number(u?.UID),
            managerCode: u?.manager || "",
            designation: u?.designation || "",
            allotted_office: u?.allotted_office || "",
            email: u?.Email || "",
            phone: u?.PhoneNumber || "",
            address: u?.Address || "",
            leaves: {
              op: Number(u?.leaves?.op ?? 0),
              va: Number(u?.leaves?.va ?? 0),
              sc: Number(u?.leaves?.sc ?? 0),
              pe: Number(u?.leaves?.pe ?? 0),
              pa: Number(u?.leaves?.pa ?? 0),
            },
            active: typeof u?.active === "boolean" ? u.active : true,
          });
        });
      }

      list.sort((a, b) => {
        const ua = Number.isFinite(a.uid) ? a.uid : 0;
        const ub = Number.isFinite(b.uid) ? b.uid : 0;
        return ub - ua;
      });

      setUsers(list);
    } catch (err) {
      console.error("Error cargando usuarios:", err);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleToggleActive = async (row) => {
    if (row.active) {
      const confirmed = window.confirm(
        `Vas a desactivar a ${row.name || "este usuario"}. ¿Deseas continuar?`
      );
      if (!confirmed) return;
    }
    try {
      await cambiarEstadoUsuario(row.id, !row.active);
      setUsers((prev) =>
        prev.map((u) => (u.id === row.id ? { ...u, active: !u.active } : u))
      );
      setNotice(
        row.active
          ? "Usuario desactivado correctamente."
          : "Usuario activado correctamente."
      );
    } catch (err) {
      console.error("Error actualizando estado:", err);
      setNotice("No se pudo cambiar el estado.");
    }
  };

  const handleConfirmModal = async (dataFromModal) => {
    if (creating) return;

    setCreating(true);
    try {
      const leavesPayload = {
        op:
          dataFromModal?.leaves?.op ??
          dataFromModal?.op ??
          dataFromModal?.OP ??
          0,
        va:
          dataFromModal?.leaves?.va ??
          dataFromModal?.va ??
          dataFromModal?.VA ??
          0,
        sc:
          dataFromModal?.leaves?.sc ??
          dataFromModal?.sc ??
          dataFromModal?.SC ??
          0,
        pe:
          dataFromModal?.leaves?.pe ??
          dataFromModal?.pe ??
          dataFromModal?.PE ??
          0,
        pa:
          dataFromModal?.leaves?.pa ??
          dataFromModal?.pa ??
          dataFromModal?.PA ??
          0,
      };

      if (editingUser?.id) {
        await actualizarUsuarioCompleto(editingUser.id, {
          UUID: dataFromModal.UUID,
          Name: dataFromModal.Name,
          PhoneNumber: dataFromModal.PhoneNumber || "",
          Address: dataFromModal.Address || "",
          manager: dataFromModal.manager || "",
          allotted_office: dataFromModal.allotted_office || "",
          designation: dataFromModal.designation || "",
          active: editingUser.active ?? true,
          leaves: leavesPayload,
        });
      } else {
        await crearUsuarioCompleto({
          UUID: dataFromModal.UUID,
          Name: dataFromModal.Name,
          Email: dataFromModal.Email,
          Password: dataFromModal.Password,
          PhoneNumber: dataFromModal.PhoneNumber || "",
          Address: dataFromModal.Address || "",
          manager: dataFromModal.manager || "",
          allotted_office: dataFromModal.allotted_office || undefined,
          designation: dataFromModal.designation || undefined,
          active: dataFromModal.active ?? true,
          leaves: leavesPayload,
        });
      }

      await loadUsers();
      setIsModalOpen(false);
      setEditingUser(null);
      setModalKey((k) => k + 1);
      setNotice(
        editingUser?.id
          ? "Usuario actualizado correctamente."
          : "Usuario creado correctamente."
      );
    } catch (err) {
      console.error("Error guardando usuario:", err);
      setNotice(
        err?.message ||
          `No se pudo ${editingUser?.id ? "actualizar" : "crear"} el usuario.`
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    if (creating) return;
    setIsModalOpen(false);
    setEditingUser(null);
    setModalKey((k) => k + 1);
  };

  const filtered = useMemo(() => {
    let base = users;
    if (statusFilter !== "all") {
      const activeRequired = statusFilter === "active";
      base = base.filter((u) => Boolean(u.active) === activeRequired);
    }
    if (!query) return base;
    const q = query.toLowerCase();
    return base.filter((u) => {
      const managerName = (u.managerCode && managers[u.managerCode]?.name) || "";
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.dni || "").toLowerCase().includes(q) ||
        (u.uid != null && String(u.uid).includes(q)) ||
        managerName.toLowerCase().includes(q)
      );
    });
  }, [users, query, managers, statusFilter]);

  const columns = [
    { key: "name", header: "Nombre", width: "30%", ellipsis: true },
    { key: "dni", header: "DNI", width: "12%", align: "center" },
    { key: "uid", header: "UID", width: "10%", align: "center" },
    {
      key: "manager",
      header: "Encargado",
      width: "34%",
      ellipsis: true,
      render: (r) => (r.managerCode && managers[r.managerCode]?.name) || "-",
    },
    {
      key: "active",
      header: "Estado",
      width: "12%",
      align: "center",
      render: (r) => (
        <span className={`state-badge ${r.active ? "state-badge--active" : "state-badge--inactive"}`}>
          {r.active ? "Activo" : "No activo"}
        </span>
      ),
    },
  ];

  const actions = (row) => (
    <div className="user-actions">
      <button
        className="icon-action-btn"
        onClick={() => {
          setEditingUser(row);
          setIsModalOpen(true);
        }}
        title="Editar usuario"
        aria-label="Editar usuario"
      >
        <FiEdit2 />
      </button>
      <label className="switch" title={row.active ? "Desactivar" : "Activar"}>
        <input
          type="checkbox"
          checked={!!row.active}
          onChange={() => handleToggleActive(row)}
          aria-label={row.active ? "Desactivar usuario" : "Activar usuario"}
        />
        <span className="slider" />
      </label>
    </div>
  );

  const toolbarLeft = (
    <div className="user-toolbar-left">
      <input
        placeholder="Buscar por nombre, DNI, UID o encargado"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="user-search"
        type="search"
      />
      <select
        className="user-status-filter"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="all">Todos</option>
        <option value="active">Activos</option>
        <option value="inactive">No activos</option>
      </select>
    </div>
  );

  const toolbarRight = (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        className="btn primary"
        onClick={() => {
          setEditingUser(null);
          setIsModalOpen(true);
        }}
        disabled={creating}
      >
        {creating ? (editingUser?.id ? "Guardando..." : "Creando...") : "+ Agregar usuario"}
      </button>
    </div>
  );

  return (
    <div className="add-user-page">
      <div className="add-user-card">
        <div className="user-list-meta">
          <span>Mostrando {filtered.length} de {users.length} usuarios</span>
          {notice ? <span className="user-notice">{notice}</span> : null}
        </div>

        <AttendanceTable
          columns={columns}
          rows={filtered}
          loading={loadingList}
          emptyText="Sin usuarios"
          actions={actions}
          toolbarLeft={toolbarLeft}
          toolbarRight={toolbarRight}
          getRowKey={(r) => r.id}
          dense={false}
        />
      </div>

      <ModalAgregarUsuario
        key={modalKey}
        open={isModalOpen}
        mode={editingUser?.id ? "edit" : "create"}
        initialData={editingUser}
        submitting={creating}
        onClose={handleCloseModal}
        onConfirm={handleConfirmModal}
      />
    </div>
  );
}
